import {
  type CSSProperties,
  type ForwardedRef,
  forwardRef,
  type MouseEvent,
  type ReactNode,
} from 'react';
import styled, { css } from 'styled-components';

import Typo from '@/components/Typography/Typo';
import { NOT_ASSIGNED_LABEL } from '@/configs/reserved-word';
import useColor from '@/hooks/domain/useColor';

import { Flex } from '../base';

interface Props {
  icon?: ReactNode;
  value?: ReactNode;
  placeholder?: string;
  onClick?: (e: MouseEvent<HTMLDivElement>) => void;
  style?: CSSProperties;
  readOnly?: boolean;
  isEllipsis?: boolean;
  suffix?: string;
}

const PropertyValue = (props: Props, ref?: ForwardedRef<HTMLDivElement>) => {
  const color = useColor();
  const placeholder = props.placeholder || NOT_ASSIGNED_LABEL;
  return (
    <PropertyValueStyle
      ref={ref}
      style={{
        cursor: props.readOnly ? 'default' : 'pointer',
        ...props.style,
      }}
      a="center"
      g={4}
      onClick={(e) => props.onClick?.(e)}
      isEllipsis={props.isEllipsis}
    >
      {props.icon && (
        <Flex a="center" style={{ fontSize: 16, textAlign: 'center' }}>
          {props.icon}
        </Flex>
      )}
      <Typo
        type="body3"
        weight="m"
        color={props.value ? 'inherit' : color.gray500}
        ellipsis={props.isEllipsis}
        multilineEllipsis={props.isEllipsis ? 1 : undefined}
      >
        {props.value || placeholder}
        {props.suffix}
      </Typo>
    </PropertyValueStyle>
  );
};

export default forwardRef(PropertyValue);

export const PropertyValueStyle = styled(Flex)<{
  isEllipsis?: boolean;
}>`
  min-height: 24px;
  padding: 2px 6px;
  border-radius: 4px;
  transition: all 0.2s;
  color: ${(props) => props.theme.colors.gray700};
  &:hover {
    background: ${(props) => props.theme.colors.ND04};
  }
  ${({ isEllipsis }) =>
    isEllipsis &&
    css`
      span {
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
        max-width: 360px;
      }
    `}
`;

export const IconOnlyStyle = styled(Flex).attrs({
  a: 'center',
  j: 'center',
})`
  width: 24px;
  height: 24px;
  border-radius: 16px;
  background-color: ${(props) => props.theme.colors.ND00};
  transition: all 0.2s;
  cursor: pointer;
  &:hover {
    background-color: ${(props) => props.theme.colors.ND04};
  }
`;
